import React, { Fragment, useEffect, useState } from "react";
import { Box, Chip, Grid } from "@material-ui/core";
import CallIcon from "@material-ui/icons/Call";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import ChromeReaderModeIcon from "@material-ui/icons/ChromeReaderMode";
import CropFreeIcon from "@material-ui/icons/CropFree";
import DateRangeIcon from "@material-ui/icons/DateRange";
import RoomOutlinedIcon from "@material-ui/icons/RoomOutlined";
import LanguageOutlinedIcon from "@material-ui/icons/LanguageOutlined";
import LinkOutlinedIcon from "@material-ui/icons/LinkOutlined";
import style from "./informations.module.scss";
import { useSelector } from "react-redux";
import { GetUrlBuilderData, selectBuilderPermissions } from "store/url-builder";
import { AdditionalInfoDialog } from "./additional-info-dialog";
import { QRCodeDialog } from "components/shared/qr-code-dialog";
import { GetCardData } from "store/card";
import { GetConfigData } from "store/config/selectors";
import { findPermission } from "utils/permissions";
import { BuilderPermissions } from "types/permissions";
import { LinksDialog } from "./links-dialog";
import { CallDialog } from "./call-dialog";

export interface InformationsProps {
  portfolioType: string;
}

export const Informations = ({ portfolioType }: InformationsProps) => {
  const portfolio = useSelector(GetUrlBuilderData);
  const portfolioCard = useSelector(GetCardData);
  const confData = useSelector(GetConfigData);
  const portfolioPermissions = useSelector(selectBuilderPermissions);

  const [numbers, setNumbers] = useState<any[]>([]);

  const [openAdditionalInfo, setOpenAdditionalInfo] = useState(false);
  const [openCallDialogue, setOpenCallDialogue] = useState(false);
  const [openLinks, setOpenLinks] = useState(false);
  const [openQrCode, setOpenQrCode] = useState(false);

  useEffect(() => {
    let _numbers = [
      portfolioCard.number,
      portfolioCard.mobile,
      portfolioCard.tollFree,
      portfolioCard.direct,
    ].filter((item) => item && item.toString().trim());
    setNumbers(_numbers);
  }, [portfolioCard]);

  return (
    <>
      <Box
        sx={{
          gridGap: 10,
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        {numbers.length > 0 && (
          <Fragment>
            {numbers.length === 1 && (
              <a target="_new" href={`tel://${numbers[0]}`}>
                <Chip
                  clickable
                  label="Call"
                  className={style.chip}
                  avatar={
                    <CallIcon style={{ color: portfolio.primaryColor.color }} />
                  }
                />
              </a>
            )}
            {numbers.length > 1 && (
              <Chip
                clickable
                label="Call"
                className={style.chip}
                onClick={() => setOpenCallDialogue(true)}
                avatar={
                  <CallIcon style={{ color: portfolio.primaryColor.color }} />
                }
              />
            )}
          </Fragment>
        )}
        {portfolioCard.email && (
          <a target="_new" href={`mailto:${portfolioCard.email}`}>
            <Chip
              clickable
              label="Email"
              className={style.chip}
              avatar={
                <MailOutlineIcon
                  style={{ color: portfolio.primaryColor.color }}
                />
              }
            />
          </a>
        )}

        {/* TODO CARD INFO MISSING PERMISION */}

        <Chip
          onClick={() => setOpenAdditionalInfo(true)}
          label="Info"
          className={style.chip}
          avatar={
            <ChromeReaderModeIcon
              style={{ color: portfolio.primaryColor.color }}
            />
          }
        />
        <Chip
          onClick={() => setOpenQrCode(true)}
          label="Code"
          className={style.chip}
          avatar={
            <CropFreeIcon style={{ color: portfolio.primaryColor.color }} />
          }
        />
        {(portfolioType === "preview" ||
          findPermission(portfolioPermissions, {
            moduleId: BuilderPermissions.calendar,
            permissionName: "visible",
          })?.objectPermissionValue) &&
          portfolio.info.calendar && (
            <a href={portfolio.info.calendar} target="_blank" rel="noreferrer">
              <Chip
                clickable
                label="Calendar"
                className={style.chip}
                avatar={
                  <DateRangeIcon
                    style={{ color: portfolio.primaryColor.color }}
                  />
                }
              />
            </a>
          )}
        {(portfolioType === "preview" ||
          findPermission(portfolioPermissions, {
            moduleId: BuilderPermissions.location,
            permissionName: "visible",
          })?.objectPermissionValue) &&
          portfolio.info.location && (
            <a
              href={`https://www.google.com/maps/place/${portfolio.info.location}`}
              target="_blank"
              rel="noreferrer"
            >
              <Chip
                clickable
                label="Navigate"
                className={style.chip}
                avatar={
                  <RoomOutlinedIcon
                    style={{ color: portfolio.primaryColor.color }}
                  />
                }
              />
            </a>
          )}
        {portfolioCard.website && (
          <a
            href={
              portfolioCard.website.startsWith("http")
                ? portfolioCard.website
                : "http://" + portfolioCard.website
            }
            target="_blank"
            rel="noreferrer"
          >
            <Chip
              clickable
              label="Website"
              className={style.chip}
              avatar={
                <LanguageOutlinedIcon
                  style={{ color: portfolio.primaryColor.color }}
                />
              }
            />
          </a>
        )}
        {(portfolioType === "preview" ||
          findPermission(portfolioPermissions, {
            moduleId: BuilderPermissions.links,
            permissionName: "visible",
          })?.objectPermissionValue) &&
          portfolio.info.links &&
          portfolio.info.links.length > 0 && (
            <Chip
              clickable
              onClick={() => setOpenLinks(true)}
              label="Links"
              className={style.chip}
              avatar={
                <LinkOutlinedIcon
                  style={{ color: portfolio.primaryColor.color }}
                />
              }
            />
          )}
      </Box>
      {openCallDialogue && (
        <CallDialog
          open={openCallDialogue}
          onClose={() => setOpenCallDialogue(false)}
          portfolioCard={portfolioCard}
          colour={portfolio.primaryColor.color}
        />
      )}
      {openAdditionalInfo && (
        <AdditionalInfoDialog
          data={portfolio.info}
          open={openAdditionalInfo}
          onClose={() => setOpenAdditionalInfo(false)}
          portfolioCard={portfolioCard}
        />
      )}
      {openLinks && (
        <LinksDialog
          data={portfolio.info.links}
          open={openLinks}
          onClose={() => setOpenLinks(false)}
        />
      )}
      {openQrCode && (
        <QRCodeDialog
          open={openQrCode}
          color={portfolio.primaryColor.color}
          onClose={() => setOpenQrCode(false)}
          value={`${confData.filesBaseUrl}/${portfolioCard.qrcodeImage}`}
        />
      )}
    </>
  );
};
